import { AcademicLeague } from "../types/battle"
import {
  ControlTier,
  ControlTierInfo,
  DominanceRewardChoice,
  DominantLeague,
  FactionTournament,
  GameTournamentKind,
  GuardianPathBonus,
  GuardianPathInfo,
  LeagueBalance,
  LeagueFinalCandidate,
  PersonalWarMilestone,
  PointControl,
  TopRewardChoice,
  WarInfluence,
  WarStatus,
} from "../types/faction"
import {
  GUARDIAN_PATH_DEFAULTS,
  normalizeGuardianPath,
} from "../constants/guardianPaths"
import { CONTROL_TIER_LABELS } from "../constants/factionInfo"
import { GuardianPath } from "../types/battle"

function pickNumber(value: unknown, fallback = 0): number {
  if (typeof value === "number" && Number.isFinite(value)) return value
  if (typeof value === "string" && value.trim()) {
    const n = Number(value.replace(",", "."))
    return Number.isFinite(n) ? n : fallback
  }
  return fallback
}

function pickString(value: unknown, fallback = ""): string {
  if (typeof value === "string") return value
  if (value != null) return String(value)
  return fallback
}

function pickBool(value: unknown, fallback = false): boolean {
  if (typeof value === "boolean") return value
  if (value === 1 || value === "1" || value === "true") return true
  if (value === 0 || value === "0" || value === "false") return false
  return fallback
}

function pickLeague(value: unknown): AcademicLeague | null {
  const raw = pickString(value).toUpperCase()
  if (raw === "TITAN" || raw === "ATLANT") return raw
  return null
}

function pickDominantLeague(value: unknown): DominantLeague {
  const raw = pickString(value).toUpperCase()
  if (raw === "TITAN" || raw === "ATLANT") return raw
  return "NEUTRAL"
}

function pickControlTier(value: unknown): ControlTier {
  const raw = pickString(value).toUpperCase()
  const tiers: ControlTier[] = [
    "PARITY",
    "EDGE",
    "DOMINANCE",
    "HEGEMONY",
    "TOTAL",
  ]
  return tiers.includes(raw as ControlTier) ? (raw as ControlTier) : "PARITY"
}

function normalizeControlTier(raw: unknown): ControlTierInfo {
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    const obj = raw as Record<string, unknown>
    const tier = pickControlTier(obj.tier ?? obj.controlTier ?? obj.control_tier)
    return {
      tier,
      label: pickString(obj.label, CONTROL_TIER_LABELS[tier]),
    }
  }
  const tier = pickControlTier(raw)
  return { tier, label: CONTROL_TIER_LABELS[tier] }
}

function normalizeGuardianPathBonus(raw: unknown): GuardianPathBonus | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const key = pickString(obj.key)
  const label = pickString(obj.label)
  if (!key && !label) return null
  const contextsRaw = obj.contexts
  const contexts = Array.isArray(contextsRaw)
    ? contextsRaw.map((item) => pickString(item)).filter(Boolean)
    : []
  return {
    key: key || label,
    value: pickNumber(obj.value),
    label: label || key,
    unit: pickString(obj.unit, "count"),
    contexts,
    implemented: pickBool(obj.implemented, true),
  }
}

export function normalizeGuardianPathInfo(
  raw: unknown,
  path?: GuardianPath | null
): GuardianPathInfo | null {
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    const obj = raw as Record<string, unknown>
    const title = pickString(obj.title)
    const description = pickString(obj.description ?? obj.desc)
    if (title || description) {
      const resolvedPath =
        normalizeGuardianPath(obj.path ?? obj.key ?? obj.guardianPath) ?? path
      if (!resolvedPath) return null
      const bonusesRaw = obj.bonuses
      const bonuses = Array.isArray(bonusesRaw)
        ? bonusesRaw
            .map(normalizeGuardianPathBonus)
            .filter((item): item is GuardianPathBonus => item != null)
        : undefined
      return {
        path: resolvedPath,
        title: title || GUARDIAN_PATH_DEFAULTS[resolvedPath].title,
        description:
          description || GUARDIAN_PATH_DEFAULTS[resolvedPath].description,
        bonuses: bonuses?.length ? bonuses : undefined,
      }
    }
  }
  if (path) return GUARDIAN_PATH_DEFAULTS[path]
  return null
}

export function normalizeLeagueBalance(raw: unknown): LeagueBalance | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const cityId = pickNumber(obj.cityId ?? obj.city_id, NaN)
  if (!Number.isFinite(cityId)) return null

  const titan = pickNumber(obj.titan ?? obj.titanCount ?? obj.titan_count)
  const atlant = pickNumber(obj.atlant ?? obj.atlantCount ?? obj.atlant_count)
  const total = pickNumber(obj.total, titan + atlant)

  const loreRaw = obj.lore
  let lore: LeagueBalance["lore"]
  if (loreRaw && typeof loreRaw === "object" && !Array.isArray(loreRaw)) {
    lore = {}
    for (const league of ["TITAN", "ATLANT"] as AcademicLeague[]) {
      const entry = (loreRaw as Record<string, unknown>)[league]
      if (entry && typeof entry === "object") {
        const e = entry as Record<string, unknown>
        lore[league] = {
          title: pickString(e.title, league),
          idea: pickString(e.idea),
          colors: pickString(e.colors),
        }
      }
    }
  }

  return {
    cityId,
    titan,
    atlant,
    total,
    titanPercent: pickNumber(obj.titanPercent ?? obj.titan_percent, total > 0 ? Math.round((titan / total) * 100) : 50),
    atlantPercent: pickNumber(obj.atlantPercent ?? obj.atlant_percent, total > 0 ? Math.round((atlant / total) * 100) : 50),
    minorityLeague: pickLeague(obj.minorityLeague ?? obj.minority_league),
    lore,
  }
}

function normalizePersonalMilestone(raw: unknown): PersonalWarMilestone | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const threshold = pickNumber(obj.threshold, NaN)
  if (!Number.isFinite(threshold)) return null
  return {
    threshold,
    coins: pickNumber(obj.coins) || undefined,
    chakra: pickNumber(obj.chakra) || undefined,
    fuseBooster: pickNumber(obj.fuseBooster ?? obj.fuse_booster) || undefined,
    claimed: pickBool(obj.claimed ?? obj.isClaimed ?? obj.is_claimed),
    reached: pickBool(obj.reached ?? obj.isReached ?? obj.is_reached),
  }
}

function normalizePointControl(raw: unknown): PointControl | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const pointId = pickString(obj.pointId ?? obj.point_id ?? obj.id)
  if (!pointId) return null
  return {
    pointId,
    dominantLeague: pickDominantLeague(
      obj.dominantLeague ?? obj.dominant_league
    ),
    titanScans: pickNumber(obj.titanScans ?? obj.titan_scans),
    atlantScans: pickNumber(obj.atlantScans ?? obj.atlant_scans),
  }
}

function normalizeWarInfluence(raw: unknown): WarInfluence {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {}
  const obj = raw as Record<string, unknown>
  const tierRaw = obj.tier ?? obj.controlTier ?? obj.control_tier
  const rewardsRaw = obj.activeRewards ?? obj.active_rewards
  return {
    tier: tierRaw ? pickControlTier(tierRaw) : null,
    isDominant: pickBool(obj.isDominant ?? obj.is_dominant),
    shopDiscountPercent: pickNumber(
      obj.shopDiscountPercent ?? obj.shop_discount_percent
    ) || undefined,
    activeRewards: Array.isArray(rewardsRaw)
      ? rewardsRaw.map((item) => pickString(item)).filter(Boolean)
      : undefined,
    profileFrameKey:
      pickString(obj.profileFrameKey ?? obj.profile_frame_key) || null,
  }
}

export function normalizeWarStatus(raw: unknown): WarStatus | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>

  const titanWarPts = pickNumber(obj.titanWarPts ?? obj.titan_war_pts)
  const atlantWarPts = pickNumber(obj.atlantWarPts ?? obj.atlant_war_pts)
  const totalWarPts = pickNumber(
    obj.totalWarPts ?? obj.total_war_pts,
    titanWarPts + atlantWarPts
  )

  const milestonesRaw = obj.personalMilestones ?? obj.personal_milestones
  const pointControlsRaw = obj.pointControls ?? obj.point_controls

  return {
    weekStart: pickString(obj.weekStart ?? obj.week_start),
    cityId: pickNumber(obj.cityId ?? obj.city_id),
    titanWarPts,
    atlantWarPts,
    totalWarPts,
    titanPercent: pickNumber(
      obj.titanPercent ?? obj.titan_percent,
      totalWarPts > 0 ? Math.round((titanWarPts / totalWarPts) * 100) : 50
    ),
    atlantPercent: pickNumber(
      obj.atlantPercent ?? obj.atlant_percent,
      totalWarPts > 0 ? Math.round((atlantWarPts / totalWarPts) * 100) : 50
    ),
    dominantLeague: pickLeague(obj.dominantLeague ?? obj.dominant_league),
    controlTier: normalizeControlTier(obj.controlTier ?? obj.control_tier),
    isUnderdog: pickBool(obj.isUnderdog ?? obj.is_underdog),
    activeRotationRewards: Array.isArray(
      obj.activeRotationRewards ?? obj.active_rotation_rewards
    )
      ? ((obj.activeRotationRewards ?? obj.active_rotation_rewards) as unknown[])
          .map((item) => pickString(item))
          .filter(Boolean)
      : [],
    personalWarPts: pickNumber(obj.personalWarPts ?? obj.personal_war_pts),
    personalMilestones: Array.isArray(milestonesRaw)
      ? milestonesRaw
          .map(normalizePersonalMilestone)
          .filter((item): item is PersonalWarMilestone => item != null)
      : [],
    counterattackQuestAvailable: pickBool(
      obj.counterattackQuestAvailable ?? obj.counterattack_quest_available
    ),
    counterattackClaimed: pickBool(
      obj.counterattackClaimed ?? obj.counterattack_claimed
    ),
    dominanceRewardAvailable: pickBool(
      obj.dominanceRewardAvailable ?? obj.dominance_reward_available
    ),
    dominanceRewardClaimed: pickBool(
      obj.dominanceRewardClaimed ?? obj.dominance_reward_claimed
    ),
    topRewardAvailable: pickBool(
      obj.topRewardAvailable ?? obj.top_reward_available
    ),
    topRewardClaimed: pickBool(
      obj.topRewardClaimed ?? obj.top_reward_claimed
    ),
    influence: normalizeWarInfluence(obj.influence),
    pointControls: Array.isArray(pointControlsRaw)
      ? pointControlsRaw
          .map(normalizePointControl)
          .filter((item): item is PointControl => item != null)
      : [],
  }
}

export function normalizeGuardianPathList(raw: unknown): GuardianPathInfo[] {
  const list = Array.isArray(raw)
    ? raw
    : raw && typeof raw === "object"
      ? ((raw as Record<string, unknown>).paths ??
          (raw as Record<string, unknown>).items ??
          [])
      : []

  if (!Array.isArray(list) || list.length === 0) {
    return Object.values(GUARDIAN_PATH_DEFAULTS)
  }

  return list
    .map((item) => {
      if (!item || typeof item !== "object") return null
      const obj = item as Record<string, unknown>
      const path = normalizeGuardianPath(obj.path ?? obj.key ?? obj.guardianPath)
      if (!path) return null
      const defaults = GUARDIAN_PATH_DEFAULTS[path]
      const bonusesRaw = obj.bonuses
      const bonuses = Array.isArray(bonusesRaw)
        ? bonusesRaw
            .map(normalizeGuardianPathBonus)
            .filter((item): item is GuardianPathBonus => item != null)
        : undefined
      return {
        path,
        title: pickString(obj.title ?? obj.name, defaults.title),
        description: pickString(obj.description ?? obj.desc, defaults.description),
        bonuses: bonuses?.length ? bonuses : undefined,
      }
    })
    .filter((item) => item != null) as GuardianPathInfo[]
}

function normalizeTournamentKind(value: unknown): GameTournamentKind {
  const raw = pickString(value).toUpperCase()
  const kinds: GameTournamentKind[] = [
    "STANDARD",
    "LEAGUE_TITAN",
    "LEAGUE_ATLANT",
    "CITY_FINAL",
  ]
  return kinds.includes(raw as GameTournamentKind)
    ? (raw as GameTournamentKind)
    : "STANDARD"
}

export function normalizeFactionTournament(raw: unknown): FactionTournament | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const id = pickString(obj.id ?? obj.tournamentId ?? obj.tournament_id)
  if (!id) return null
  return {
    id,
    kind: normalizeTournamentKind(obj.kind ?? obj.type ?? obj.tournamentKind),
    title: pickString(obj.title ?? obj.name, "Турнир"),
    cityId: pickNumber(obj.cityId ?? obj.city_id) || undefined,
    cityName: pickString(obj.cityName ?? obj.city_name) || null,
    weekStart: pickString(obj.weekStart ?? obj.week_start) || null,
    status: pickString(obj.status) || null,
  }
}

export function normalizeLeagueFinalCandidate(
  raw: unknown
): LeagueFinalCandidate | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const userId = pickNumber(obj.userId ?? obj.user_id, NaN)
  if (!Number.isFinite(userId)) return null
  return {
    userId,
    nickname: pickString(obj.nickname, "Хранитель"),
    publicGameCode: pickString(obj.publicGameCode ?? obj.public_game_code) || undefined,
    warPts: pickNumber(obj.warPts ?? obj.war_pts ?? obj.pts),
    rank: pickNumber(obj.rank, 0),
    academicLeague: pickLeague(obj.academicLeague ?? obj.academic_league),
    guardianPath: normalizeGuardianPath(obj.guardianPath ?? obj.guardian_path),
    profileFrameKey:
      pickString(obj.profileFrameKey ?? obj.profile_frame_key) || null,
  }
}

export function normalizeTopRewardChoice(value: unknown): TopRewardChoice | null {
  const raw = pickString(value).toLowerCase()
  if (raw === "coins" || raw === "chakra" || raw === "fuse") return raw
  return null
}

export function normalizeDominanceRewardChoice(
  value: unknown
): DominanceRewardChoice | null {
  const raw = pickString(value).toLowerCase()
  if (raw === "fuse" || raw === "style") return raw
  return null
}

export function applyPointControlsToLocationPoints<
  T extends { id: string; dominantLeague?: DominantLeague; titanScans?: number; atlantScans?: number },
>(points: T[], controls: PointControl[]): T[] {
  if (!controls.length) return points
  const byId = new Map(controls.map((item) => [String(item.pointId), item]))
  return points.map((point) => {
    const control = byId.get(String(point.id))
    if (!control) return point
    return {
      ...point,
      dominantLeague: control.dominantLeague,
      titanScans: control.titanScans,
      atlantScans: control.atlantScans,
    }
  })
}
